import { Fragment } from 'react'
import { type UmsatzZahlart } from '../api'
import { euro, ZAHLART_FOLGE, zahlartArt, zahlartFarbe, zahlartName } from '../format'
import { Anteile } from './Anteile'
import { Tipp } from './Hinweis'

interface Gruppe {
  art: string
  umsatz: number
  anzahl: number
  trinkgeld: number
  namen: UmsatzZahlart[]
}

/**
 * Die Zahlarten eines Zeitraums — zweistufig.
 *
 * Oben die **Art**: Bar, Karte, Essensgutschein, Gutschein, Auf Rechnung, Sonstiges.
 * Darunter die Namen, die die Kasse vergibt.
 *
 * Der Grund für die zwei Ebenen ist ein fachlicher. „Tap to Pay auf dem
 * iPhone" und „Lightspeed Payments" sind beides Kartenzahlungen — die
 * Kasse selbst führt sie unter demselben `ZAHLART_TYP` (Kreditkarte),
 * sie unterscheiden sich nur im Weg: das eine läuft im Buchungsgerät,
 * das andere über ein EC-Terminal. Wer wissen will, wie viel bargeldlos
 * hereinkommt, liest die obere Ebene; wer wissen will, ob ein Standort
 * sein Terminal noch braucht, die untere. Beides ist eine echte Frage,
 * deshalb bleiben beide Ebenen stehen.
 *
 * Die zweite Ebene steht nur da, wenn es etwas zu unterscheiden gibt:
 * Bei nur einem Namen entfällt die Wiederholung, außer wenn der Name den Amadeus-Hinweis trägt.
 */
export function Zahlarten({ zeilen, gutscheine, berichtVorhanden }: {
  zeilen: UmsatzZahlart[]
  berichtVorhanden: boolean
  gutscheine: { kauf: string; einloesung: string }
}) {
  const kauf = Number(gutscheine.kauf)
  const mitGutschein = kauf !== 0

  if (!berichtVorhanden) {
    return <div className="leer">Kein Zahlungsbericht für diesen Zeitraum und diese Standorte vorhanden.</div>
  }

  if (!zeilen.length && !mitGutschein) {
    return <div className="leer">Keine auswertbaren Zahlungen im Zahlungsbericht.</div>
  }

  const nach = new Map<string, Gruppe>()
  for (const z of zeilen) {
    const g = nach.get(z.art)
      ?? { art: z.art, umsatz: 0, anzahl: 0, trinkgeld: 0, namen: [] }
    g.umsatz += Number(z.umsatz)
    g.anzahl += z.anzahl
    g.trinkgeld += Number(z.trinkgeld)
    g.namen.push(z)
    nach.set(z.art, g)
  }
  const gruppen = [...nach.values()].sort(
    (a, b) => ZAHLART_FOLGE.indexOf(a.art) - ZAHLART_FOLGE.indexOf(b.art))
  for (const g of gruppen) g.namen.sort((a, b) => Number(b.umsatz) - Number(a.umsatz))

  const gibtWas = gruppen.length > 0
  const vorgaenger = (name: string) =>
    name === 'Amadeus Token' || name === 'Alt Gutschein Amadeus'
  const debitorTipp = 'Hier ist kein Geld geflossen, sondern eine Forderung entstanden. Gezeigt wird der Zugang dieses Tages, kein offener Forderungsbestand. Der Ausgleich läuft über die Buchhaltung.'
  const summe = gruppen.reduce((s, g) => s + g.umsatz, 0)
  const prozentBasis = summe || 1

  return (
    <div>
      <Anteile
        anteile={gruppen.map((g) => ({
          name: zahlartArt(g.art), wert: g.umsatz, anzahl: g.anzahl,
          farbe: zahlartFarbe(g.art),
          tipp: g.art === 'DEBTOR' ? debitorTipp : undefined,
        }))}
        formatiere={euro} />

      {gibtWas && (
        <table style={{ marginTop: 12 }}>
              <tbody>
                {gruppen.map((g) => (
                  <Fragment key={g.art}>
                    <tr>
                      <td><b>{zahlartArt(g.art)}</b>
                        {g.art === 'DEBTOR' && <Tipp text={debitorTipp} />}
                      </td>
                      <td className="r zahl">{euro(g.umsatz)}</td>
                      <td className="r zahl dezent">
                        {((g.umsatz / prozentBasis) * 100).toFixed(1).replace('.', ',')} %
                      </td>
                    </tr>
                    {(g.namen.length > 1 || g.namen.some((z) =>
                      z.kassennamen > 1 || vorgaenger(z.zahlart)))
                      && g.namen.map((z) => (
                      <tr key={`${g.art}-${z.zahlart}`}>
                        <td style={{ paddingLeft: 18 }} className="dezent">
                          {zahlartName(z.zahlart)}
                          {vorgaenger(z.zahlart) && (
                            <Tipp text="Gutschein aus der Vorgängerkasse — der Gegenwert wurde dort eingenommen." />
                          )}
                        </td>
                        <td className="r zahl dezent">{euro(z.umsatz)}</td>
                        <td className="r zahl dezent">
                          {((Number(z.umsatz) / prozentBasis) * 100).toFixed(1)
                            .replace('.', ',')} %
                        </td>
                      </tr>
                    ))}
                    {g.trinkgeld !== 0 && (
                      <tr className="dezent">
                        <td>dazu Trinkgeld</td>
                        <td className="r zahl">{euro(g.trinkgeld)}</td>
                        <td />
                      </tr>
                    )}
                  </Fragment>
                ))}
                <tr style={{ borderTop: '2px solid var(--linie)', fontWeight: 600 }}>
                  <td><strong>Zusammen, nach Rabatt</strong>{' '}
                    <Tipp text={'Die Summe der Zahlungen darüber, und die Grundlage dieser Prozente. Sie steht auf dem Umsatz nach Rabatt — was dazugehört, zeigt die Kachel „Stornos und Rabatte". „Auf Rechnung" und eingelöste Gutscheine zählen mit: dort ist kein Geld geflossen, der Umsatz ist aber entstanden. Die Kachel „Gesamtumsatz" rechnet anders und kann deshalb abweichen.'} />
                  </td>
                  <td className="r zahl">{euro(summe)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
      )}

      {mitGutschein && (
        <div style={{ marginTop: 14 }}>
          <table>
            <tbody>
              {kauf !== 0 && (
                <tr>
                  <td>
                    Gutscheine verkauft{' '}
                    <Tipp text="Beim Kauf fließt das Geld in einer anderen Zahlart. Deshalb steht der Verkauf außerhalb der Verteilung." />
                  </td>
                  <td className="r zahl">{euro(Math.abs(kauf))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
